/**
 * BulkActionsDropdown - Dropdown for bulk lead operations
 * Supports changing status, city, sector, and country for multiple leads
 */

import React, { useState, useRef, useEffect } from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { useBulkUpdateLeads } from "../hooks/useLeads";
import type { LeadStatus, BulkUpdateData } from "../Lead.types";
import { LEAD_STATUS_CONFIG } from "../Lead.types";

interface BulkActionsDropdownProps {
  selectedIds: string[];
  cities: string[];
  sectors: string[];
  countries: string[];
  onComplete: () => void;
  disabled?: boolean;
}

type MenuSection = "main" | "status" | "city" | "sector" | "country";

export function BulkActionsDropdown({
  selectedIds,
  cities,
  sectors,
  countries,
  onComplete,
  disabled = false,
}: BulkActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<MenuSection>("main");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bulkUpdateMutation = useBulkUpdateLeads();
  const addToast = useAppStore((state) => state.addToast);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveSection("main");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleUpdate = async (updates: BulkUpdateData) => {
    if (selectedIds.length === 0) return;

    try {
      await bulkUpdateMutation.mutateAsync({ leadIds: selectedIds, updates });
      addToast({
        message: `Updated ${selectedIds.length} lead${selectedIds.length > 1 ? "s" : ""}`,
        type: "success",
        duration: 3000,
      });
      setIsOpen(false);
      setActiveSection("main");
      onComplete();
    } catch (error) {
      addToast({
        message: "Failed to update leads",
        type: "error",
        duration: 5000,
      });
      console.error("Failed to bulk update leads:", error);
    }
  };

  const renderMainMenu = () => (
    <div className="py-1">
      <button
        onClick={() => setActiveSection("status")}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Change Status
        </span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <button
        onClick={() => setActiveSection("city")}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Change City
        </span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <button
        onClick={() => setActiveSection("sector")}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Change Sector
        </span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <button
        onClick={() => setActiveSection("country")}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Change Country
        </span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  const renderBackButton = (label: string) => (
    <button
      onClick={() => setActiveSection("main")}
      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 border-b border-gray-100"
    >
      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span className="font-medium text-gray-700">{label}</span>
    </button>
  );

  const renderStatusMenu = () => (
    <div className="py-1">
      {renderBackButton("Change Status")}
      <div className="max-h-64 overflow-y-auto">
        {Object.entries(LEAD_STATUS_CONFIG).map(([status, config]) => (
          <button
            key={status}
            onClick={() => handleUpdate({ status: status as LeadStatus })}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
          >
            <span className={`inline-block w-2 h-2 rounded-full ${config.dot}`} />
            {config.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderCityMenu = () => (
    <div className="py-1">
      {renderBackButton("Change City")}
      <button
        onClick={() => handleUpdate({ city: "" })}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-gray-500 italic"
      >
        Clear City
      </button>
      <div className="max-h-64 overflow-y-auto">
        {cities.map((city) => (
          <button
            key={city}
            onClick={() => handleUpdate({ city })}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
          >
            {city}
          </button>
        ))}
      </div>
      {cities.length === 0 && (
        <div className="px-4 py-2 text-sm text-gray-400 italic">No cities available</div>
      )}
    </div>
  );

  const renderSectorMenu = () => (
    <div className="py-1">
      {renderBackButton("Change Sector")}
      <button
        onClick={() => handleUpdate({ sector: "" })}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-gray-500 italic"
      >
        Clear Sector
      </button>
      <div className="max-h-64 overflow-y-auto">
        {sectors.map((sector) => (
          <button
            key={sector}
            onClick={() => handleUpdate({ sector })}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
          >
            {sector}
          </button>
        ))}
      </div>
      {sectors.length === 0 && (
        <div className="px-4 py-2 text-sm text-gray-400 italic">No sectors available</div>
      )}
    </div>
  );

  const renderCountryMenu = () => (
    <div className="py-1">
      {renderBackButton("Change Country")}
      <button
        onClick={() => handleUpdate({ country: "" })}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-gray-500 italic"
      >
        Clear Country
      </button>
      <div className="max-h-64 overflow-y-auto">
        {countries.map((country) => (
          <button
            key={country}
            onClick={() => handleUpdate({ country })}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
          >
            {country}
          </button>
        ))}
      </div>
      {countries.length === 0 && (
        <div className="px-4 py-2 text-sm text-gray-400 italic">No countries available</div>
      )}
    </div>
  );

  const isLoading = bulkUpdateMutation.isPending;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setActiveSection("main");
        }}
        disabled={disabled || isLoading}
        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Updating...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Bulk Edit
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {activeSection === "main" && renderMainMenu()}
          {activeSection === "status" && renderStatusMenu()}
          {activeSection === "city" && renderCityMenu()}
          {activeSection === "sector" && renderSectorMenu()}
          {activeSection === "country" && renderCountryMenu()}
        </div>
      )}
    </div>
  );
}
